import { useEffect, useState } from "react";
import { sb } from "../lib/supabase";
import { useIsMobile } from "../lib/useIsMobile";

export function InvoicePreviewModal({ invoice, onClose }) {
  const isMobile = useIsMobile();
  const [preview, setPreview] = useState("");
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [sent,    setSent]    = useState(false);
  const [error,   setError]   = useState("");

  // Auto-generate preview on open
  useEffect(()=>{ generatePreview(); },[]); // eslint-disable-line react-hooks/exhaustive-deps

  async function generatePreview() {
    setLoading(true); setError("");
    try {
      const res = await sb.fn("send-invoice", { invoice_id:invoice.id, send_email:false });
      if (res.error) { setError(res.error); }
      else { setPreview(res.html); }
    } catch(e) { console.error(e); setError("Failed to generate preview"); }
    setLoading(false);
  }

  async function downloadPDF() {
    // Open in new window for browser print-to-PDF
    const win = window.open("","_blank");
    if (win) { win.document.write(preview||"<p>Generate preview first</p>"); win.document.close(); setTimeout(()=>win.print(),500); }
  }

  async function sendEmail() {
    setSending(true); setError("");
    try {
      const res = await sb.fn("send-invoice", { invoice_id:invoice.id, send_email:true });
      if (res.error||res.emailError) { setError(res.error||res.emailError); }
      else { setSent(true); }
    } catch(e) { setError("Failed to send email"); }
    setSending(false);
  }

  // Phone: ONE scroller. The .modal sheet scrolls; the body flows; the iframe is sized
  // to its own content (srcDoc is same-origin, so its document is readable) and the
  // 600px-wide email template is zoomed down to the sheet width instead of scrolling
  // sideways. Desktop keeps the fixed 500px iframe inside the 60vh body.
  function fitIframe(e) {
    if (!isMobile) return;
    const fr = e.target;
    try {
      const doc = fr.contentDocument;
      if (!doc) return;
      const de = doc.documentElement, body = doc.body;
      const frameW   = fr.clientWidth;
      const contentW = Math.max(de.scrollWidth, body ? body.scrollWidth : 0);
      if (frameW > 0 && contentW > frameW && body) { body.style.zoom = String(frameW / contentW); }
      de.style.overflowX = "hidden";
      if (body) body.style.overflowX = "hidden";
      const h = Math.max(de.scrollHeight, body ? body.scrollHeight : 0);
      if (h) fr.style.height = (h + 16) + "px";
    } catch (err) { /* cross-origin or not ready — keep the fallback height */ }
  }

  return (
    <div className="modal-overlay" onClick={e=>e.target===e.currentTarget&&onClose()}>
      {/* The inline 92vh cap would beat the theme's 85dvh phone sheet — only apply it on desktop. */}
      <div className="modal" style={isMobile?{maxWidth:700}:{maxWidth:700,maxHeight:"92vh"}}>
        <div className="modal-header">
          <h3>📄 Invoice {invoice.invoice_number}</h3>
          <button className="close-btn" onClick={onClose}>×</button>
        </div>

        <div className="modal-body" style={isMobile?undefined:{maxHeight:"60vh",overflowY:"auto"}}>
          {loading&&<div style={{padding:40,textAlign:"center",color:"#8993a4"}}>Generating preview...</div>}
          {!loading&&preview&&(
            <iframe srcDoc={preview} onLoad={fitIframe}
              style={{width:"100%",height:isMobile?"60dvh":500,border:"1px solid #e3e8ef",borderRadius:6,display:"block"}} title="Invoice Preview"/>
          )}
          {!loading&&!preview&&<div style={{padding:40,textAlign:"center",color:"#8993a4"}}>Loading preview...</div>}
          {error&&<div style={{background:"#ffebe6",border:"1px solid #ffbdad",borderRadius:6,padding:"8px 12px",fontSize:12,color:"#bf2600",marginTop:12}}>⚠️ {error}</div>}
          {sent &&<div style={{background:"#e3fcef",border:"1px solid #abf5d1",borderRadius:6,padding:"8px 12px",fontSize:12,color:"#006644",marginTop:12}}>✅ Invoice emailed successfully to {invoice.client_name}!</div>}
        </div>

        {/* Phone: Download PDF is window.open + print (no fit for iOS Safari) — hidden;
            Send to Client takes its own full-width row so it's never cut off. */}
        <div className="modal-footer" style={isMobile?{flexWrap:"wrap"}:undefined}>
          <button className="btn btn-ghost" onClick={onClose}>Close</button>
          {preview&&!isMobile&&<button className="btn btn-ghost" onClick={downloadPDF}>⬇️ Download PDF</button>}
          <button className="btn btn-gold" onClick={sendEmail} disabled={sending||sent} style={isMobile?{flex:"1 1 auto",justifyContent:"center"}:undefined}>
            {sent?"✓ Sent!":sending?"Sending...":"📧 Send to Client"}
          </button>
        </div>
      </div>
    </div>
  );
}
